// Fixture data for the mock backend (see ./adapter.ts).
//
// The card screens are the ones that matter here: ID -> Cards -> {StudentCard,
// DNECard, DNEBack}. They read `subUser.card`, so every field those components
// touch is populated with something realistic:
//
//   - `expiry` must parse and be in the future, and `isic_data.status` /
//     `fesn_data.status` must not be "Pending", or the cards render greyed out
//     as EXPIRED/PENDING.
//   - `images.front` is the cardholder photo, `display_images.{front,back}` are
//     the card artwork. On the real backend these are opaque ids fetched from
//     the image microservice; here they are already-resolved URLs, which
//     useFetchImage passes straight through.

import { Image } from "react-native";

/**
 * Resolve a `require()`d image to a URL string. Webpack hands back a string (or
 * a module object), Metro hands back a numeric asset id — this covers both, so
 * the fixtures work if the mocks are ever switched on for a native build.
 */
const assetUri = (mod: any): string => {
	if (typeof mod === "string") return mod;
	if (mod && typeof mod === "object" && typeof mod.uri === "string") return mod.uri;
	if (mod && typeof mod === "object" && typeof mod.default === "string") return mod.default;
	return Image.resolveAssetSource(mod)?.uri ?? "";
};

export const MOCK_PROFILE_PHOTO = assetUri(
	require("../../assets/mock_users/Bernardo_Pfp.jpg")
);
const DNE_FRONT = assetUri(require("../../assets/images/cards/newisicfrente.png"));
const DNE_BACK = assetUri(require("../../assets/images/cards/dne-back.png"));
// Stand-ins for banner artwork that would otherwise be fetched from the image
// microservice — without them the discount rails fire requests at a host this
// build never reaches.
const BANNER_A = assetUri(require("../../assets/images/discountPizzaHut.png"));
const BANNER_B = assetUri(require("../../assets/images/destaque2.png"));

export const MOCK_USER_ID = "6512f0a4e1d2c30012a4b8f1";
const MAIN_USER_ID = "6512f0a4e1d2c30012a4b8f0";

/** A structurally valid JWT so the token-refresh interceptor in store/api.ts
 *  can decode it. Signature is meaningless — nothing verifies it here. */
const fakeJwt = (subject: string): string => {
	const encode = (obj: object) => {
		const json = JSON.stringify(obj);
		const base64 =
			typeof btoa === "function"
				? btoa(json)
				: Buffer.from(json, "utf8").toString("base64");
		return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
	};
	const header = encode({ alg: "HS256", typ: "JWT" });
	const payload = encode({
		iat: 1700000000,
		exp: 4102444800, // 2100-01-01, so the refresh path never triggers
		user_id: subject,
	});
	return `${header}.${payload}.mock-signature-not-verified`;
};

export const MOCK_TOKEN = fakeJwt(MAIN_USER_ID);
export const MOCK_REFRESH_TOKEN = fakeJwt(`${MAIN_USER_ID}-refresh`);

export const MOCK_CARD = {
	name: "Bernardo Hauer Santos",
	institution: "Universidade Federal do Rio Grande do Norte",
	institution_id: "20210041237",
	course: "Ciência da Computação",
	education_level: "Ensino Superior",
	city: "Natal",
	uf: "RN",
	cpf: "12345678909",
	rg: "0021458963",
	dob: "15/03/2001",
	expiry: "2028-12-31",
	status: "Active",
	type_of_card: "DNE",
	image_id: MOCK_PROFILE_PHOTO,
	images: {
		front: MOCK_PROFILE_PHOTO,
	},
	display_images: {
		front: DNE_FRONT,
		back: DNE_BACK,
	},
	isic_data: {
		error: null,
		isic_card_number: "S 424 991 234 567 8",
		status: "Active",
	},
	fesn_data: {
		error: null,
		fesn_id: "FESN-2024-RN-0041237",
		fesn_national_id: "RN00412372024",
		fesn_qrcode_link: "https://e1.cl/RN00412372024",
		status: "Active",
	},
};

export const MOCK_SUB_USER = {
	id: MOCK_USER_ID,
	main_user_id: MAIN_USER_ID,
	name: "Bernardo Hauer Santos",
	position: "Estudante",
	cpf: "12345678909",
	rg: "0021458963",
	dob: "15/03/2001",
	city: "Natal",
	uf: "RN",
	course: "Ciência da Computação",
	education_level: "Ensino Superior",
	institution: "Universidade Federal do Rio Grande do Norte",
	institution_id: "20210041237",
	profile_image_id: MOCK_PROFILE_PHOTO,
	created_at: "2024-02-11T13:04:22.000Z",
	updated_at: "2025-01-08T09:41:10.000Z",
	card: MOCK_CARD,
};

export const MOCK_USER_INFO = {
	id: MAIN_USER_ID,
	name: "Bernardo Hauer Santos",
	email: "bernardo@hauertech.io",
	account_type: "student",
	aggregate_users: [MOCK_SUB_USER],
};

export const MOCK_MAIN_USER = {
	token: MOCK_TOKEN,
	refresh_token: MOCK_REFRESH_TOKEN,
	user_info: MOCK_USER_INFO,
};

// Discounts/announcements are out of scope for this build — the card page is
// what matters — but the Home screen renders rails from them, so a couple of
// entries keep it from looking broken. Their banner images are ids the mock
// image endpoint does not serve, so the tiles render as empty frames.
export const MOCK_DISCOUNTS = [
	{
		id: "discount-1",
		name: "Cinemark",
		link: "https://www.cinemark.com.br",
		description: "Meia-entrada garantida em todas as sessões.",
		category: ["Entretenimento"],
		region: "Nacional",
		discountType: ["Presencial"],
		date: "2025-01-05",
		popularity: true,
		imageId: BANNER_A,
		bannerImageId: BANNER_A,
		about: "Apresente a carteirinha na bilheteria.",
		observations: "Não cumulativo com outras promoções.",
		expiration_date: "2027-12-31",
		voucher: "ISIC2025",
		position: "1",
	},
	{
		id: "discount-2",
		name: "Livraria Cultura",
		link: "https://www.livrariacultura.com.br",
		description: "15% de desconto em livros selecionados.",
		category: ["Educação"],
		region: "Nacional",
		discountType: ["Online"],
		date: "2025-02-14",
		popularity: true,
		imageId: BANNER_B,
		bannerImageId: BANNER_B,
		about: "Use o cupom no carrinho.",
		observations: "Válido para livros físicos.",
		expiration_date: "2027-12-31",
		voucher: "ISICLIVRO",
		position: "2",
	},
];

export const MOCK_ANNOUNCEMENTS: any[] = [];

export const MOCK_INSTITUTIONS = [
	{ id: "1", name: "Universidade Federal do Rio Grande do Norte" },
	{ id: "2", name: "Universidade Federal de Pernambuco" },
	{ id: "3", name: "Universidade de São Paulo" },
];
